import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Loader2, Video, Eye, EyeOff, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { TenerifeMap } from '@/components/weather/TenerifeMap';

const EMPTY = { name_es: '', name_en: '', name_de: '', latitude: 28.3, longitude: -16.5, source_type: 'skyline', source_id: '', page_url: '', heading: 0, fov: 70, coastal: true, wave_factor: 1, is_active: true, bot_notes: '', alt_forecast: null, main_forecast_name: null };
const EMPTY_ALT = { name_es: '', name_en: '', name_de: '', latitude: 28.2723, longitude: -16.6424, elevation: 3715 };

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs text-gray-600">{label}</Label>{children}</div>
);

const WebcamForm = ({ initial, cams, onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const preview = { ...form, id: initial?.id || 'new', latitude: Number(form.latitude) || 28.3, longitude: Number(form.longitude) || -16.5 };
  const others = cams.filter((c) => c.id !== preview.id && c.is_active);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), heading: Number(form.heading), fov: Number(form.fov), wave_factor: Number(form.wave_factor) || 1 };
    payload.alt_forecast = form.alt_forecast
      ? { ...form.alt_forecast, latitude: Number(form.alt_forecast.latitude), longitude: Number(form.alt_forecast.longitude), elevation: form.alt_forecast.elevation === '' || form.alt_forecast.elevation == null ? null : Number(form.alt_forecast.elevation) }
      : null;
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.order; delete payload.zone_id;
    try {
      if (initial?.id) await api.adminUpdateWebcam(initial.id, payload);
      else await api.adminCreateWebcam(payload);
      toast.success('Webcam guardada');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail?.missing ? `Faltan: ${err.response.data.detail.missing.join(', ')}` : 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 p-5 space-y-5" data-testid="webcam-form">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[#1D1D1F]">{initial?.id ? 'Editar webcam' : 'Nueva webcam'}</h3>
        <button type="button" onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-700" data-testid="webcam-form-close"><X size={18} /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Nombre ES"><Input value={form.name_es} onChange={(e) => set('name_es', e.target.value)} required data-testid="webcam-name-es" /></Field>
            <Field label="Nombre EN"><Input value={form.name_en} onChange={(e) => set('name_en', e.target.value)} data-testid="webcam-name-en" /></Field>
            <Field label="Nombre DE"><Input value={form.name_de} onChange={(e) => set('name_de', e.target.value)} data-testid="webcam-name-de" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Latitud"><Input type="number" step="0.0001" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} required data-testid="webcam-lat" /></Field>
            <Field label="Longitud"><Input type="number" step="0.0001" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} required data-testid="webcam-lon" /></Field>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <Field label="Fuente">
              <select value={form.source_type} onChange={(e) => set('source_type', e.target.value)} className="w-full h-10 border border-gray-200 px-2 text-sm bg-white" data-testid="webcam-source-type">
                <option value="skyline">Skyline</option><option value="youtube">YouTube</option><option value="image">Imagen</option>
              </select>
            </Field>
            <Field label={form.source_type === 'skyline' ? 'ID Skyline (número)' : form.source_type === 'youtube' ? 'ID vídeo YouTube' : 'URL imagen https'}>
              <Input value={form.source_id} onChange={(e) => set('source_id', e.target.value)} required data-testid="webcam-source-id" />
            </Field>
          </div>
          <Field label="Página del stream (Abrir cámara)"><Input value={form.page_url} onChange={(e) => set('page_url', e.target.value)} data-testid="webcam-page-url" /></Field>
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.coastal} onCheckedChange={(v) => set('coastal', v)} data-testid="webcam-coastal" /> Costera (oleaje)</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.is_active} onCheckedChange={(v) => set('is_active', v)} data-testid="webcam-active" /> Activa</label>
          </div>
          <Field label="Notas internas para el bot">
            <textarea value={form.bot_notes || ''} onChange={(e) => set('bot_notes', e.target.value)} rows={2} className="w-full border border-amber-200 bg-amber-50/40 p-2 text-sm" data-testid="webcam-bot-notes" />
          </Field>
          <div className="border border-dashed border-gray-200 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Switch checked={!!form.alt_forecast} onCheckedChange={(v) => set('alt_forecast', v ? { ...EMPTY_ALT } : null)} data-testid="webcam-alt-toggle" />
              Segundo punto de previsión (p. ej. Pico del Teide)
            </label>
            {form.alt_forecast && (
              <div className="grid grid-cols-2 gap-2">
                <Field label="Nombre del punto principal en el selector (ES)"><Input value={form.main_forecast_name?.name_es || ''} onChange={(e) => set('main_forecast_name', e.target.value ? { ...(form.main_forecast_name || {}), name_es: e.target.value } : null)} placeholder="p. ej. Cañadas del Teide" data-testid="webcam-main-label" /></Field>
                <Field label="Nombre del punto principal (EN)"><Input value={form.main_forecast_name?.name_en || ''} onChange={(e) => set('main_forecast_name', { ...(form.main_forecast_name || { name_es: form.name_es }), name_en: e.target.value })} data-testid="webcam-main-label-en" /></Field>
                <Field label="Nombre ES"><Input value={form.alt_forecast.name_es} onChange={(e) => set('alt_forecast', { ...form.alt_forecast, name_es: e.target.value })} required data-testid="webcam-alt-name" /></Field>
                <Field label="Altitud (m)"><Input type="number" value={form.alt_forecast.elevation ?? ''} onChange={(e) => set('alt_forecast', { ...form.alt_forecast, elevation: e.target.value })} data-testid="webcam-alt-elev" /></Field>
                <Field label="Latitud"><Input type="number" step="0.0001" value={form.alt_forecast.latitude} onChange={(e) => set('alt_forecast', { ...form.alt_forecast, latitude: e.target.value })} required data-testid="webcam-alt-lat" /></Field>
                <Field label="Longitud"><Input type="number" step="0.0001" value={form.alt_forecast.longitude} onChange={(e) => set('alt_forecast', { ...form.alt_forecast, longitude: e.target.value })} required data-testid="webcam-alt-lon" /></Field>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-[#FDFBF7] border border-gray-100">
            <TenerifeMap cams={[...others, preview]} selectedId={preview.id} language="es" showLabels={false} />
          </div>
          <Field label={`Dirección del cono: ${form.heading}° (0 = Norte, 90 = Este)`}>
            <Slider value={[Number(form.heading)]} min={0} max={360} step={5} onValueChange={([v]) => set('heading', v)} data-testid="webcam-heading" />
          </Field>
          <Field label={`Amplitud del cono: ${form.fov}°`}>
            <Slider value={[Number(form.fov)]} min={10} max={180} step={5} onValueChange={([v]) => set('fov', v)} data-testid="webcam-fov" />
          </Field>
          {form.coastal && (
            <Field label={`Abrigo de la playa: ×${Number(form.wave_factor ?? 1).toFixed(1)} (1 = mar abierto · 0.4 = bahía con espigón)`}>
              <Slider value={[Number(form.wave_factor ?? 1)]} min={0.1} max={1.5} step={0.1} onValueChange={([v]) => set('wave_factor', v)} data-testid="webcam-wave-factor" />
            </Field>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" disabled={saving} className="bg-[#38B2AC] hover:bg-[#2C8F8A]" data-testid="webcam-save">{saving && <Loader2 size={16} className="animate-spin mr-2" />}Guardar</Button>
      </div>
    </form>
  );
};

const AdminWebcams = () => {
  const [cams, setCams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.adminGetWebcams().then((r) => setCams(r.data)).catch(() => toast.error('No se pudieron cargar las webcams')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggleActive = async (cam) => {
    try { await api.adminUpdateWebcam(cam.id, { is_active: !cam.is_active }); load(); } catch { toast.error('No se pudo actualizar'); }
  };
  const remove = async (cam) => {
    if (!window.confirm(`¿Eliminar la webcam «${cam.name_es}»?`)) return;
    try { await api.adminDeleteWebcam(cam.id); toast.success('Webcam eliminada'); load(); } catch { toast.error('No se pudo eliminar'); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#38B2AC]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-webcams">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-lg text-[#1D1D1F] flex items-center gap-2"><Video size={18} className="text-[#38B2AC]" /> Webcams del mapa</h2>
          <p className="text-sm text-gray-500">Puntos y conos de visión de la pantalla Tiempo. Grokbot también puede gestionarlas por la API.</p>
        </div>
        {!editing && <Button onClick={() => setEditing({})} className="bg-[#38B2AC] hover:bg-[#2C8F8A]" data-testid="webcam-add-btn"><Plus size={16} className="mr-1" /> Nueva</Button>}
      </div>

      {editing && <WebcamForm initial={editing} cams={cams} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}

      <div className="bg-white border border-gray-200 divide-y divide-gray-100">
        {cams.map((cam) => (
          <div key={cam.id} className={`flex items-center gap-3 px-4 py-3 ${cam.is_active ? '' : 'opacity-50'}`} data-testid={`webcam-row-${cam.id}`}>
            <div className="w-10 h-10 bg-[#38B2AC]/10 flex items-center justify-center text-[#38B2AC] flex-shrink-0 text-xs font-bold">{cam.heading}°</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1D1D1F] truncate">{cam.name_es}</p>
              <p className="text-xs text-gray-500 truncate">{cam.source_type} · {cam.source_id} · cono {cam.fov}° · {cam.coastal ? 'costera' : 'interior'}</p>
            </div>
            <button onClick={() => toggleActive(cam)} className="p-2 text-gray-500 hover:text-[#38B2AC]" title={cam.is_active ? 'Desactivar' : 'Activar'} data-testid={`webcam-toggle-${cam.id}`}>{cam.is_active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
            <button onClick={() => setEditing(cam)} className="p-2 text-gray-500 hover:text-[#38B2AC]" data-testid={`webcam-edit-${cam.id}`}><Pencil size={16} /></button>
            <button onClick={() => remove(cam)} className="p-2 text-gray-500 hover:text-red-600" data-testid={`webcam-delete-${cam.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminWebcams;
